// src/features/records/components/shared/CompactLinkedRecords.js
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as LinkIcon, ExternalLink } from 'lucide-react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styles from './CompactLinkedRecords.module.css';
import { sanitizeText } from '../../../../utils/sanitize';
import useLinkedRecordPreview from '../../hooks/useLinkedRecordPreview';

/** Utilities */
const isExternalLink = (link) => link.targetType === 'external';
const recordHrefFor = (id) => `/record/${encodeURIComponent(id)}`;
const stableKeyFor = (link) => link.id ?? `${link.targetType}:${link.targetId}`;

/**
 * CompactLinkedRecordItem - Individual link item within the compact display
 * Non-ready states are rendered as non-interactive, accessible placeholders.
 */
const CompactLinkedRecordItem = React.memo(function CompactLinkedRecordItem({ link }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, title } = useLinkedRecordPreview(link);

  const external = isExternalLink(link);
  const href = useMemo(
    () => (external ? link.targetId : recordHrefFor(link.targetId)),
    [external, link.targetId]
  );

  const disabled = status !== 'ready';
  const Icon = external ? ExternalLink : LinkIcon;

  const resolvedTitle = sanitizeText(title);
  const fallbackTitle = t('records.detail.linked_records.open_link', 'Open link');
  const displayTitle = resolvedTitle || fallbackTitle;
  const ariaLabel = resolvedTitle
    ? t('records.detail.linked_records.open_link_with_title', {
        title: resolvedTitle,
        defaultValue: 'Open {{title}}'
      })
    : fallbackTitle;

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (disabled) return;

    const isPrimaryClick = e.button === 0;
    const hasModifier = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

    if (!external && isPrimaryClick && !hasModifier) {
      e.preventDefault();
      navigate(recordHrefFor(link.targetId));
    }
    // external: allow default (new tab if target set)
  }, [disabled, external, link.targetId, navigate]);

  // Non-ready states: render a placeholder that absorbs interactions so the card doesn't navigate
  if (disabled) {
    const handleDisabledInteraction = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    return (
      <span
        className={`${styles.linkItem} ${styles.linkDisabled}`}
        aria-disabled="true"
        aria-busy={status === 'loading' ? 'true' : undefined}
        title={displayTitle}
        tabIndex={-1}
        onClick={handleDisabledInteraction}
        onMouseDown={handleDisabledInteraction}
      >
        <Icon className={styles.linkIcon} size={14} />
        <span className={styles.linkTitle}>{displayTitle}</span>
      </span>
    );
  }

  // Ready & actionable
  const anchorProps = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <a
      className={styles.linkItem}
      href={href}
      onClick={handleClick}
      aria-label={ariaLabel}
      title={displayTitle}
      {...anchorProps}
    >
      <Icon className={styles.linkIcon} size={14} />
      <span className={styles.linkTitle}>{displayTitle}</span>
    </a>
  );
});

CompactLinkedRecordItem.propTypes = {
  link: PropTypes.shape({
    id: PropTypes.string,
    targetId: PropTypes.string.isRequired,
    targetType: PropTypes.oneOf(['record', 'external']).isRequired,
  }).isRequired
};

/**
 * CompactLinkedRecords - Compact list, shows up to `maxItems` with a "+N more" action
 */
const CompactLinkedRecords = React.memo(function CompactLinkedRecords({
  links = [],
  maxItems = 3
}) {
  const { t } = useTranslation();
  if (!Array.isArray(links) || links.length === 0) return null;

  const displayLinks = links.slice(0, maxItems);
  const remainingCount = links.length - displayLinks.length;

  return (
    <div className={styles.compactLinkedRecords}>
      {displayLinks.map((link) => (
        <CompactLinkedRecordItem key={stableKeyFor(link)} link={link} />
      ))}

      {remainingCount > 0 && (
        <span className={styles.moreCount}>
          {t('records.detail.linked_records.more_count', {
            count: remainingCount,
            defaultValue: '+{{count}} more'
          })}
        </span>
      )}
    </div>
  );
});

CompactLinkedRecords.propTypes = {
  links: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    targetId: PropTypes.string.isRequired,
    targetType: PropTypes.oneOf(['record', 'external']).isRequired,
  })),
  maxItems: PropTypes.number
};

export default CompactLinkedRecords;
